import { useEffect, useState } from "react";
import { locationApi } from "../api/masterApi";

/**
 * The locations this login works in, fetched once per page load and shared
 * by every screen. A login with no location assigned works in all of them
 * ({@code all: true}); otherwise only in its own.
 *
 * Returns { all, locations, companyLocations, defaultLocationId, mainLocationId, loading }.
 * `locations` are { id, locationName, city, isMain, isActive } — the login's own.
 * `companyLocations` are every active location of the company, { id, locationName }
 * — where stock can be sent.
 */
let cache = null;
let pending = null;

function load() {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = locationApi.mine()
      .then(r => {
        const d = r.data?.data ?? r.data ?? {};
        cache = {
          all: !!d.all,
          locations: d.locations || [],
          companyLocations: d.companyLocations || [],
          defaultLocationId: d.defaultLocationId ?? null,
          mainLocationId: d.mainLocationId ?? null,
        };
        return cache;
      })
      .catch(() => ({ all: true, locations: [], companyLocations: [], defaultLocationId: null, mainLocationId: null }))
      .finally(() => { pending = null; });
  }
  return pending;
}

/** Drops the cached list — after a location is added, renamed or made main. */
export function refreshMyLocations() {
  cache = null;
  return load();
}

export default function useMyLocations() {
  const [state, setState] = useState(() => cache
    ? { ...cache, loading: false }
    : { all: true, locations: [], companyLocations: [], defaultLocationId: null, mainLocationId: null, loading: true });

  useEffect(() => {
    let alive = true;
    load().then(d => { if (alive) setState({ ...d, loading: false }); });
    return () => { alive = false; };
  }, []);

  return state;
}
